<script setup>
  import { onMounted, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Info } from "lucide-vue-next";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Label } from "@/components/ui/label";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useSurveyCardsSettingsStore } from "@stores/surveyCardsSettings";
  import { useCurrentUserStore } from "@stores/currentUser.js";
  import { useSurveyStore } from "@stores/survey/survey";
  import { capitalize } from "@utils/toolBox";

  const humanizeCardType = (type) => capitalize(type?.split("_").slice(1).join(" "));

  const surveyCardsSettingsStore = useSurveyCardsSettingsStore();
  const surveyStore = useSurveyStore();
  const currentUserStore = useCurrentUserStore();

  const { cards, areSetupCardsAdminOnlyModel, areSetupCardsHiddenInChartsModel } =
    storeToRefs(surveyCardsSettingsStore);

  const { setSetupCards, handleUpdateSetupCardAdminOnly, updateSetupCardIsHiddenInChart } = surveyCardsSettingsStore;

  const { isAiEnabled, isSignificanceShown } = storeToRefs(surveyStore);

  const { updateEnableAi, updateShowSignificance } = surveyStore;

  const { isAdmin } = storeToRefs(currentUserStore);

  const activeNames = ref(["general"]);

  onMounted(async () => {
    await setSetupCards();
  });
</script>

<template>
  <ThreePanePage
    :span-left="2"
    :span-center="8"
    :span-right="2"
    data-cy="survey-results-settings-page">
    <template #center>
      <Card class="p-6">
        <CardHeader>
          <CardTitle class="heading-page">Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion
            v-model="activeNames"
            type="multiple">
            <!-- General -->
            <AccordionItem value="general">
              <AccordionTrigger class="text-base font-semibold">General</AccordionTrigger>
              <AccordionContent class="space-y-4 pt-2">
                <div class="flex items-center gap-2">
                  <Checkbox
                    id="enable-ai"
                    :model-value="isAiEnabled"
                    @update:model-value="updateEnableAi" />
                  <Label
                    for="enable-ai"
                    class="cursor-pointer font-medium">
                    Enable AI generated features
                  </Label>
                </div>
                <div class="flex items-center gap-2">
                  <Checkbox
                    id="show-significance"
                    :model-value="isSignificanceShown"
                    @update:model-value="updateShowSignificance" />
                  <Label
                    for="show-significance"
                    class="cursor-pointer font-medium">
                    Show significance
                  </Label>
                  <Tooltip side="top">
                    <Info class="size-4 text-muted-foreground" />
                    <template #content>
                      Significance indicates that a difference in results is unlikely
                      <br />
                      due to chance. Green and red arrows denote significant
                      <br />
                      differences compared to the complementary population.
                    </template>
                  </Tooltip>
                </div>
              </AccordionContent>
            </AccordionItem>

            <!-- Cards -->
            <AccordionItem value="cards">
              <AccordionTrigger class="text-base font-semibold">Cards</AccordionTrigger>
              <AccordionContent class="pt-2">
                <div class="divide-y divide-border">
                  <div
                    v-for="(card, index) in cards"
                    :key="`card-${card.id}`"
                    class="py-3">
                    <div class="flex items-center gap-4">
                      <span class="min-w-0 flex-1 truncate text-sm"
                        >{{ card.name }} ({{ humanizeCardType(card.type) }})</span
                      >
                      <div class="flex shrink-0 items-center gap-6">
                        <div
                          v-if="isAdmin"
                          class="flex w-24 items-center gap-2">
                          <Checkbox
                            :id="`admin-only-${card.id}`"
                            :model-value="areSetupCardsAdminOnlyModel[index]"
                            @update:model-value="handleUpdateSetupCardAdminOnly(index, $event)" />
                          <Label
                            :for="`admin-only-${card.id}`"
                            class="cursor-pointer whitespace-nowrap">
                            Admin only
                          </Label>
                        </div>
                        <div class="flex w-28 items-center gap-2">
                          <Checkbox
                            :id="`hide-charts-${card.id}`"
                            :model-value="areSetupCardsHiddenInChartsModel[index]"
                            @update:model-value="updateSetupCardIsHiddenInChart(index, $event)" />
                          <Label
                            :for="`hide-charts-${card.id}`"
                            class="cursor-pointer whitespace-nowrap">
                            Hide in charts
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </template>
  </ThreePanePage>
</template>
